import { useCallback } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';

/* A tab strip, keyboard-driven per the ARIA pattern rather than only clickable.
 *
 * **Arrow keys move between tabs and the strip holds one tab stop**, which is the part a
 * hand-rolled row of buttons always gets wrong: with each tab focusable, a keyboard user tabbing
 * through a dataset page walks eight controls before reaching the content. `tabIndex={-1}` on the
 * inactive tabs plus arrow handling is what the pattern prescribes, and it is why this is a component
 * rather than a CSS class.
 *
 * Generic over the tab id so a screen's tab union is checked at the call site — a `panel` prop
 * naming a tab that does not exist is a compile error rather than a blank page.
 */

export interface TabDefinition<Id extends string> {
  readonly id: Id;
  readonly label: string;
  /** A count beside the label. `undefined` shows nothing; `0` shows a zero, which is a real answer. */
  readonly count?: number | undefined;
}

export interface TabsProps<Id extends string> {
  readonly tabs: readonly TabDefinition<Id>[];
  readonly active: Id;
  readonly onSelect: (id: Id) => void;
  /** Names the strip for assistive technology, e.g. "Dataset sections". */
  readonly label: string;
}

export function Tabs<Id extends string>({
  tabs,
  active,
  onSelect,
  label,
}: TabsProps<Id>): ReactElement {
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      if (delta === 0) return;
      event.preventDefault();
      const index = tabs.findIndex((tab) => tab.id === active);
      /* Wraps, because a strip that stops at the ends makes a keyboard user reverse direction to
         reach the tab they can see is one step away. */
      const next = tabs[(index + delta + tabs.length) % tabs.length];
      if (next !== undefined) onSelect(next.id);
    },
    [tabs, active, onSelect],
  );

  return (
    <div className="lq-tabs" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          className="lq-tab"
          aria-selected={tab.id === active}
          tabIndex={tab.id === active ? 0 : -1}
          onClick={() => {
            onSelect(tab.id);
          }}
        >
          {tab.label}
          {tab.count !== undefined ? (
            <span className="lq-tab__count">{tab.count.toLocaleString()}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}
